import { useState, useEffect, useMemo, useCallback } from "react";
import { PRINTERS } from "../config/printers";
import { useWebSocket } from "../hooks/useWebSocket";
import { getEmployeeNameById } from "../utils/employeeNames";

/**
 * PA Dashboard - Printer Status Dashboard
 * Top: Idle printers grouped by type (S80600, LEF, VO/UV Rollfed)
 * Bottom: Active printers with jobs listed underneath
 */

// Printer type groupings for display
const PRINTER_TYPE_GROUPS = {
  'S80600': { label: 'S80600 Roll-fed', order: 1 },
  'LEF': { label: 'LEF Flatbed UV', order: 2 },
  'VO': { label: 'VersaObject', order: 3 },
  'UV Rollfed': { label: 'UV Roll-fed', order: 4 },
};

export default function PADashboard({ onBackToApp }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Selected item for detail panel
  const [selectedItem, setSelectedItem] = useState(null);

  // Track expanded printers (collapsed by default)
  const [expandedPrinters, setExpandedPrinters] = useState({});

  // Update current time every second for live elapsed time
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch team sessions
  const fetchSessions = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch("/api/pm/team/sessions", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error("Failed to load sessions");
      }

      const data = await res.json();
      setSessions(data.sessions || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error("Error loading sessions:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket session event handler
  const handleSessionEvent = useCallback((data) => {
    console.log("📡 PA Dashboard WebSocket event:", data.event);
    fetchSessions();
  }, [fetchSessions]);

  // Initialize WebSocket connection
  const { isConnected: wsConnected } = useWebSocket({
    onSessionEvent: handleSessionEvent,
    enabled: true,
  });

  // Initial load and fallback polling
  useEffect(() => {
    fetchSessions();
    const pollMs = wsConnected ? 30000 : 5000;
    const interval = setInterval(fetchSessions, pollMs);
    return () => clearInterval(interval);
  }, [fetchSessions, wsConnected]);

  // Filter sessions for print-related operations only
  const paSessions = useMemo(() => {
    return sessions.filter(
      (s) => s.operationCode === "PRINT-MACH" || s.operationCode === "COLOR DEV"
    );
  }, [sessions]);

  // Calculate elapsed time with live updates (supports both session objects and startTime strings)
  const formatElapsed = (sessionOrStartTime) => {
    let diffMs = 0;

    if (typeof sessionOrStartTime === "object" && sessionOrStartTime !== null) {
      // Session object passed - use accumulated seconds (floor to remove decimals)
      const session = sessionOrStartTime;
      const accumulated = Math.floor(session.accumulatedSeconds || 0) * 1000; // convert to ms

      if (session.state === "running" && session.startTime) {
        const start = new Date(session.startTime);
        const currentSegment = currentTime - start;
        diffMs = accumulated + (currentSegment > 0 ? currentSegment : 0);
      } else if (session.durationSeconds) {
        diffMs = Math.floor(session.durationSeconds) * 1000;
      } else {
        diffMs = accumulated;
      }
    } else {
      // Legacy: just startTime string passed
      if (!sessionOrStartTime) return "0:00";
      const start = new Date(sessionOrStartTime);
      diffMs = currentTime - start;
    }

    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  // Get active printer IDs
  const activePrinterIds = useMemo(() => {
    return new Set(paSessions.map(s => s.printer?.id).filter(Boolean));
  }, [paSessions]);

  // Group idle printers by type
  const idlePrintersByType = useMemo(() => {
    const idlePrinters = PRINTERS.filter(p => !activePrinterIds.has(p.id));
    const grouped = {};

    for (const printer of idlePrinters) {
      const type = printer.model || 'Other';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(printer);
    }

    // Sort by order defined in PRINTER_TYPE_GROUPS
    return Object.entries(grouped)
      .sort((a, b) => {
        const orderA = PRINTER_TYPE_GROUPS[a[0]]?.order || 99;
        const orderB = PRINTER_TYPE_GROUPS[b[0]]?.order || 99;
        return orderA - orderB;
      });
  }, [activePrinterIds]);

  // Group active printers with their sessions
  const activePrintersWithSessions = useMemo(() => {
    const grouped = {};

    for (const session of paSessions) {
      const printerId = session.printer?.id;
      if (!printerId) continue;

      if (!grouped[printerId]) {
        const printer = PRINTERS.find(p => p.id === printerId) || {
          id: printerId,
          name: session.printer?.name || printerId,
          model: 'Unknown',
        };
        grouped[printerId] = {
          printer,
          sessions: [],
        };
      }
      grouped[printerId].sessions.push(session);
    }

    // Sort by number of running jobs (most active first)
    return Object.values(grouped).sort((a, b) => {
      const aRunning = a.sessions.filter(s => s.state === 'running').length;
      const bRunning = b.sessions.filter(s => s.state === 'running').length;
      return bRunning - aRunning;
    });
  }, [paSessions]);

  // Calculate stats
  const stats = useMemo(() => ({
    running: paSessions.filter(s => s.state === "running").length,
    paused: paSessions.filter(s => s.state === "paused").length,
    idlePrinters: PRINTERS.length - activePrinterIds.size,
    activePrinters: activePrinterIds.size,
  }), [paSessions, activePrinterIds]);

  // Get selected session details
  const detailData = useMemo(() => {
    if (!selectedItem) return null;
    const session = paSessions.find(s => (s.ticketId || s.jobId) === selectedItem);
    return session || null;
  }, [selectedItem, paSessions]);

  const card = "bg-gray-300 border border-gray-200 rounded-xl shadow-lg";

  return (
    <div className="flex-1 bg-slate-100 text-gray-900 flex flex-col overflow-auto">
      {/* Stats Bar */}
      <div className="bg-slate-100 border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-sm">Running: <strong>{stats.running}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-sm">Paused: <strong>{stats.paused}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-slate-200"></div>
            <span className="text-sm">Idle Printers: <strong>{stats.idlePrinters}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-sm">Active Printers: <strong>{stats.activePrinters}</strong></span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-4 flex gap-4">
        {/* Left Panel - Main Content */}
        <div className={`flex-1 min-w-0 space-y-6 ${detailData ? "max-w-[65%]" : ""}`}>
          {loading && sessions.length === 0 && (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          )}

          {error && (
            <div className="text-center py-12 text-red-400">Error: {error}</div>
          )}

          {!loading && !error && (
            <>
              {/* IDLE PRINTERS SECTION */}
              <section>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-200"></span>
                  Idle Printers ({stats.idlePrinters})
                </h2>

                {idlePrintersByType.length === 0 ? (
                  <div className={`${card} p-4 text-center text-gray-500`}>
                    All printers are currently active!
                  </div>
                ) : (
                  <div className="space-y-4">
                    {idlePrintersByType.map(([type, printers]) => (
                      <div key={type}>
                        <h3 className="text-xs font-medium text-gray-500 mb-2 pl-1">
                          {PRINTER_TYPE_GROUPS[type]?.label || type}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {printers.map((printer) => (
                            <div
                              key={printer.id}
                              className={`${card} p-3 flex items-center gap-3 opacity-60 hover:opacity-80 transition-opacity`}
                            >
                              {printer.icon && (
                                <img
                                  src={`${import.meta.env.BASE_URL}printers/${printer.icon}.png`}
                                  alt=""
                                  className="w-10 h-10 rounded-full object-cover border border-gray-300 grayscale"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div>
                                <div className="font-medium text-sm">{printer.name}</div>
                                <div className="text-xs text-gray-500">Idle</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* ACTIVE PRINTERS SECTION */}
              <section>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  Active Printers ({stats.activePrinters})
                </h2>

                {activePrintersWithSessions.length === 0 ? (
                  <div className={`${card} p-4 text-center text-gray-500`}>
                    No active print jobs
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activePrintersWithSessions.map(({ printer, sessions: printerSessions }) => {
                      const runningCount = printerSessions.filter(s => s.state === 'running').length;
                      const pausedCount = printerSessions.filter(s => s.state === 'paused').length;
                      const isExpanded = expandedPrinters[printer.id];

                      // Get primary session info for collapsed view
                      const primarySession = printerSessions[0];
                      const operatorName = primarySession ? getEmployeeNameById(primarySession.employeeId) : "—";
                      const quoteNum = primarySession?.quoteNumber;
                      const projectName = primarySession?.projectName || primarySession?.jobName;

                      return (
                        <div key={printer.id} className={`${card} overflow-hidden`}>
                          {/* Printer Header - clickable to expand/collapse */}
                          <button
                            onClick={() => setExpandedPrinters(prev => ({ ...prev, [printer.id]: !prev[printer.id] }))}
                            className={`w-full px-4 py-3 flex items-center justify-between text-left transition-colors hover:bg-slate-200 ${
                              runningCount > 0 ? "bg-green-900/20" : "bg-yellow-900/20"
                            }`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Expand/Collapse indicator */}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? "rotate-90" : ""}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>

                              {printer.icon && (
                                <img
                                  src={`${import.meta.env.BASE_URL}printers/${printer.icon}.png`}
                                  alt=""
                                  className="w-10 h-10 rounded-full object-cover border-2 border-white/30 flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div className="min-w-0">
                                <div className="font-semibold text-lg">{printer.name}</div>
                                {/* Summary info when collapsed */}
                                {!isExpanded && (
                                  <div className="text-xs text-gray-700 truncate">
                                    <span className="text-purple-600 font-medium">{operatorName}</span>
                                    {quoteNum && <span className="text-gray-500"> • Q{quoteNum}</span>}
                                    {projectName && <span className="text-gray-500"> • {projectName}</span>}
                                  </div>
                                )}
                                {isExpanded && (
                                  <div className="text-xs text-gray-500">
                                    {printer.model} • {printerSessions.length} job{printerSessions.length !== 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-3 flex-shrink-0">
                              {runningCount > 0 && (
                                <span className="flex items-center gap-1 text-sm text-green-400">
                                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                  {runningCount}
                                </span>
                              )}
                              {pausedCount > 0 && (
                                <span className="flex items-center gap-1 text-sm text-yellow-400">
                                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                  {pausedCount}
                                </span>
                              )}
                              {/* Show elapsed time of primary session when collapsed */}
                              {!isExpanded && primarySession && (
                                <span className={`font-mono text-sm ${
                                  primarySession.state === "running" ? "text-green-400" : "text-yellow-400"
                                }`}>
                                  {formatElapsed(primarySession.startTime)}
                                </span>
                              )}
                            </div>
                          </button>

                          {/* Session Rows - only shown when expanded */}
                          {isExpanded && (
                            <div className="divide-y divide-gray-100">
                              {printerSessions.map((session) => {
                                const sessionKey = session.ticketId || session.jobId;
                                const isSelected = selectedItem === sessionKey;

                                return (
                                  <button
                                    key={sessionKey}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedItem(isSelected ? null : sessionKey);
                                    }}
                                    className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-slate-200 transition-colors text-left ${
                                      isSelected ? "bg-purple-900/20 ring-1 ring-purple-500/50" : ""
                                    }`}
                                  >
                                    {/* Status indicator */}
                                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                      session.state === "running" ? "bg-green-500 animate-pulse" : "bg-yellow-500"
                                    }`}></span>

                                    {/* Job info */}
                                    <div className="flex-1 min-w-0 flex items-center gap-4">
                                      <span className="font-mono text-sm text-purple-600 w-28 truncate">
                                        {session.jobNumber || session.jobId}
                                      </span>
                                      <span className="text-sm text-gray-700 truncate flex-1">
                                        {session.jobName || session.projectName || "—"}
                                      </span>
                                    </div>

                                    {/* Quantity */}
                                    <span className="text-xs text-gray-500 w-16 text-right">
                                      {session.qty ? `Qty: ${session.qty}` : ""}
                                    </span>

                                    {/* Employee */}
                                    <span className="text-xs text-gray-500 w-24 truncate text-right">
                                      {getEmployeeNameById(session.employeeId)?.split(" ")[0] || "—"}
                                    </span>

                                    {/* Elapsed time */}
                                    <span className={`font-mono text-sm w-20 text-right ${
                                      session.state === "running" ? "text-green-400" : "text-yellow-400"
                                    }`}>
                                      {formatElapsed(session)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Unassigned Sessions (no printer) */}
              {paSessions.filter(s => !s.printer?.id).length > 0 && (
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    No Printer Assigned ({paSessions.filter(s => !s.printer?.id).length})
                  </h2>

                  <div className={`${card} overflow-hidden`}>
                    <div className="divide-y divide-gray-100">
                      {paSessions.filter(s => !s.printer?.id).map((session) => {
                        const sessionKey = session.ticketId || session.jobId;
                        const isSelected = selectedItem === sessionKey;

                        return (
                          <button
                            key={sessionKey}
                            onClick={() => setSelectedItem(isSelected ? null : sessionKey)}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-slate-200 transition-colors text-left ${
                              isSelected ? "bg-purple-900/20 ring-1 ring-purple-500/50" : ""
                            }`}
                          >
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              session.state === "running" ? "bg-green-500 animate-pulse" : "bg-yellow-500"
                            }`}></span>

                            <div className="flex-1 min-w-0 flex items-center gap-4">
                              <span className="font-mono text-sm text-purple-600 w-28 truncate">
                                {session.jobNumber || session.jobId}
                              </span>
                              <span className="text-sm text-gray-700 truncate flex-1">
                                {session.jobName || session.projectName || "—"}
                              </span>
                            </div>

                            <span className="text-xs text-gray-500 w-24 truncate text-right">
                              {getEmployeeNameById(session.employeeId)?.split(" ")[0] || "—"}
                            </span>

                            <span className={`font-mono text-sm w-20 text-right ${
                              session.state === "running" ? "text-green-400" : "text-yellow-400"
                            }`}>
                              {formatElapsed(session)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {/* Right Panel - Detail View */}
        {detailData && (
          <div className={`${card} w-[350px] flex-shrink-0 p-4 overflow-y-auto max-h-[calc(100vh-180px)]`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Job Details</h3>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-500 hover:text-gray-900 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xs text-gray-500">Job Number</div>
                <div className="font-mono text-lg text-purple-600">
                  {detailData.jobNumber || detailData.jobId}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500">Job Name</div>
                <div className="text-gray-900">
                  {detailData.jobName || detailData.projectName || "—"}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500">Status</div>
                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${
                    detailData.state === "running"
                      ? "bg-green-100 text-green-600"
                      : "bg-yellow-100 text-yellow-600"
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      detailData.state === "running" ? "bg-green-500 animate-pulse" : "bg-yellow-500"
                    }`}></span>
                    {detailData.state === "running" ? "Running" : "Paused"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Elapsed</div>
                  <div className="font-mono text-lg">{formatElapsed(detailData.startTime)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500">Operator</div>
                  <div className="text-gray-900">{getEmployeeNameById(detailData.employeeId) || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Quantity</div>
                  <div className="text-gray-900">{detailData.qty || "—"}</div>
                </div>
              </div>

              {detailData.printer && (
                <div>
                  <div className="text-xs text-gray-500">Printer</div>
                  <div className="flex items-center gap-2 mt-1">
                    {detailData.printer.icon && (
                      <img
                        src={`${import.meta.env.BASE_URL}printers/${detailData.printer.icon}.png`}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover border border-gray-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div>
                      <div className="text-gray-900">{detailData.printer.name}</div>
                      <div className="text-xs text-gray-500">{detailData.printer.model}</div>
                    </div>
                  </div>
                </div>
              )}

              {detailData.quoteNumber && (
                <div>
                  <div className="text-xs text-gray-500">Quote</div>
                  <div className="text-gray-900">{detailData.quoteNumber}</div>
                </div>
              )}

              {detailData.orderNumber && (
                <div>
                  <div className="text-xs text-gray-500">Order</div>
                  <div className="text-gray-900">{detailData.orderNumber}</div>
                </div>
              )}

              {detailData.operationCode && (
                <div>
                  <div className="text-xs text-gray-500">Operation</div>
                  <div className="text-gray-900">{detailData.operationCode}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
